import { Component, OnInit, OnDestroy, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize } from 'rxjs';
import { AuthService } from '../../services/auth.service';
import { BrandLogoComponent } from '../../shared/brand-logo/brand-logo.component';

interface Step {
  key: 'auth' | 'verify' | 'success' | 'error';
  label: string;
  description: string;
}

@Component({
  selector: 'app-loading',
  standalone: true,
  imports: [CommonModule, BrandLogoComponent],
  templateUrl: './loading.component.html',
  styleUrls: ['./loading.component.css'],
})
export class LoadingComponent implements OnInit, OnDestroy {
  readonly steps: Step[] = [
    { key: 'auth',    label: 'Authentification', description: 'Préparation de la session…' },
    { key: 'verify',  label: 'Vérification',     description: 'Validation de votre email en cours…' },
    { key: 'success', label: 'Bienvenue',        description: 'Tout est prêt, on vous redirige.' },
  ];

  readonly currentIndex = signal<number>(0);
  readonly progress = signal<number>(0);
  readonly statusMessage = signal<string>('Préparation de la session…');
  readonly isError = signal<boolean>(false);

  private progressTimer?: any;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private auth: AuthService,
  ) {}

  ngOnInit(): void {
    this.startProgressLoop();

    const token = this.route.snapshot.queryParamMap.get('token');
    if (!token) {
      this.statusMessage.set('Aucun token reçu. Redirection vers l\'accueil…');
      this.scheduleRedirect('/', 2500);
      return;
    }

    this.advanceTo(1);
    this.auth
      .validateEmail(token)
      .pipe(finalize(() => this.stopProgressLoop()))
      .subscribe({
        next: () => {
          this.advanceTo(2);
          this.statusMessage.set('Validation réussie ! Redirection en cours…');
          this.completeProgress();
          this.scheduleRedirect('/CompleteSignUp', 1600);
        },
        error: () => {
          this.isError.set(true);
          this.statusMessage.set('Échec de la validation. Redirection vers l\'accueil…');
          this.scheduleRedirect('/Accueil', 3000);
        },
      });
  }

  ngOnDestroy(): void {
    this.stopProgressLoop();
  }

  isStepActive(idx: number): boolean { return idx === this.currentIndex(); }
  isStepDone(idx: number): boolean { return idx < this.currentIndex(); }

  private advanceTo(idx: number): void {
    this.currentIndex.set(idx);
    const step = this.steps[idx];
    if (step) this.statusMessage.set(step.description);
  }

  private startProgressLoop(): void {
    if (this.progressTimer) return;
    this.progressTimer = setInterval(() => {
      const current = this.progress();
      if (current < 88) {
        // Easing pseudo-aléatoire pour donner un effet "vie"
        const increment = Math.max(0.6, 5 * (1 - current / 100));
        this.progress.set(Math.min(88, current + increment));
      }
    }, 280);
  }

  private completeProgress(): void {
    this.stopProgressLoop();
    const finish = setInterval(() => {
      const current = this.progress();
      if (current < 100) {
        this.progress.set(Math.min(100, current + 3));
      } else {
        clearInterval(finish);
      }
    }, 30);
  }

  private stopProgressLoop(): void {
    if (this.progressTimer) {
      clearInterval(this.progressTimer);
      this.progressTimer = undefined;
    }
  }

  private scheduleRedirect(path: string, delayMs: number): void {
    setTimeout(() => this.router.navigateByUrl(path), delayMs);
  }
}
